import { accountKitApi } from "@nerdjs/account-kit";
import { ErrorCode, Purchases, PurchasesError } from "@revenuecat/purchases-js";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { proEntitlementID } from "../../constants";
import { formOpenSelector, setFormOpen } from "../../reducers/rhapsody";
import { local420Api } from "../../redux/api/local420Api";
import { useCurrentUser } from "../../redux/user/userHook";
import { trackProEvent } from "../proAnalytics/proAnalytics";

/**
 * Presents the RevenueCat paywall when the "upgradeOpen" form flag flips on.
 * The paywall UI, package list, prices, and trial copy come from the paywall
 * published in the RevenueCat dashboard — the same one the mobile app shows —
 * so design changes ship from the dashboard without a portal deploy. Checkout
 * runs through RevenueCat Web Billing.
 *
 * @returns {ReactElement} RhapsodyProUpgrade page
 */
export function RhapsodyProUpgrade() {
  const open = useSelector(formOpenSelector("upgradeOpen"));
  const dispatch = useDispatch();
  const { me: user } = useCurrentUser();
  const presenting = useRef(false);

  useEffect(() => {
    if (!open || presenting.current) return;
    presenting.current = true;
    trackProEvent("get_pro_tapped");

    let settled = false;
    const close = (dismissEvent?: string) => {
      if (settled) return;
      settled = true;
      if (dismissEvent) trackProEvent(dismissEvent);
      presenting.current = false;
      dispatch(setFormOpen({ formID: "upgradeOpen", isOpen: false }));
    };

    let purchases: Purchases;
    try {
      purchases = Purchases.getSharedInstance();
    } catch (e) {
      close();
      return;
    }

    purchases
      .presentPaywall({
        customerEmail: user?.email,
        onNavigateToUrl: (url) => window.open(url, "_blank", "noopener"),
        onBack: (closePaywall) => {
          closePaywall();
          close("paywall_dismissed");
        },
        listener: {
          onPurchaseStarted: (rcPackage) =>
            trackProEvent("paywall_purchase_started", {
              package: rcPackage.identifier,
            }),
          onPurchaseCancelled: () => trackProEvent("paywall_cancelled"),
          onPurchaseError: (error) =>
            trackProEvent("paywall_error", { error: String(error) }),
        },
      })
      .then((result) => {
        if (proEntitlementID in result.customerInfo.entitlements.active) {
          trackProEvent("paywall_purchase_completed", {
            package: result.selectedPackage.identifier,
          });
          dispatch(accountKitApi.util.invalidateTags(["user"]));
          dispatch(local420Api.util.invalidateTags(["members"]));
        }
        close();
      })
      .catch((e) => {
        if (
          e instanceof PurchasesError &&
          e.errorCode == ErrorCode.UserCancelledError
        ) {
          close("paywall_dismissed");
        } else {
          trackProEvent("paywall_error", {
            error:
              e instanceof PurchasesError ? String(e.errorCode) : "unknown",
          });
          close();
        }
      });
  }, [open]);

  return <></>;
}
